import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, matchPath, matchRoutes, useLocation } from 'react-router-dom';

/**
 * UX-85 — fil d'Ariane cliquable, PARTAGÉ par web-admin, web-parent et
 * web-student. Trois pièces :
 *
 * - `BreadcrumbProvider` + `Breadcrumbs`, montés UNE SEULE FOIS par app, juste
 *   au-dessus de `<Routes>` (App.tsx), au même titre que `SkipToContentLink` ;
 * - une CONFIG DÉCLARATIVE par app (`apps/<app>/src/breadcrumbs.ts`) : pattern
 *   de route → parents statiques + libellé de la page courante. Les libellés
 *   viennent des MÊMES constantes que la Sidebar de l'app (`lib/navigation.ts`),
 *   jamais d'une seconde liste recopiée à la main qui divergerait ;
 * - `useBreadcrumb(label)`, appelé par une page dont l'URL porte un segment
 *   dynamique (`:classId`, `:studentId`…) pour remplacer le libellé générique
 *   de la config par la donnée RÉELLE une fois chargée.
 *
 * ⚠️ POURQUOI PAS `useMatches()` / `handle.crumb` (la recette officielle de
 * React Router) : `useMatches` n'existe qu'avec un DATA ROUTER
 * (`createBrowserRouter` + `RouterProvider`). Les trois apps sont en mode
 * DÉCLARATIF (`BrowserRouter` + `<Routes>` à plat, FND-17) — `useMatches`
 * y lève une erreur. Migrer les ~60 routes vers un data router aurait été une
 * refonte du routage hors périmètre, pour un seul besoin d'affichage. Et même
 * avec, les routes sont PLATES : `/classes/:classId/timetable` n'est pas
 * imbriquée sous `/classes`, l'introspection ne trouverait aucun parent. La
 * hiérarchie est une décision PRODUIT (ce que la Sidebar présente), pas une
 * propriété de l'URL : elle doit donc être écrite, pas devinée.
 *
 * Choix de conception, dans l'ordre où ils interviennent :
 *
 * 1. CORRESPONDANCE : `matchRoutes` (disponible hors data router) sur la
 *    `location` COURANTE. Une route atteinte via `<Navigate replace>` (ex.
 *    `/reenrollments` → `/enrollments?tab=reenrollments`) est donc vue à sa
 *    DESTINATION finale, jamais à son URL de départ — aucun code dédié.
 * 2. PAGES SANS FIL : une route absente de la config n'affiche rien. C'est ce
 *    qui exclut les pages d'authentification et d'accueil (Login, Register,
 *    ForgotPassword, ResetPassword, Home) : il suffit de ne pas les déclarer.
 *    Un fil réduit à la seule page courante n'est pas affiché non plus : sans
 *    parent vers qui remonter, il ne ferait que répéter le `<h1>`.
 * 3. PLUSIEURS PARENTS POSSIBLES : une même page peut être atteinte depuis
 *    deux menus (l'emploi du temps d'une classe, depuis « Gestion
 *    pédagogique » ou depuis « Emploi du temps » côté enseignant). La config
 *    déclare des `alternatives`, chacune avec son `from` (pattern de la page
 *    d'où l'on arrive). Le provider retient, PAR ENTRÉE D'HISTORIQUE
 *    (`location.key`), le dernier chemin DISTINCT visité : un retour
 *    navigateur retrouve le fil d'origine, et un simple changement de query
 *    string (`?mode=edit`) garde la page d'origine. Sans origine connue (lien
 *    profond, rechargement), on prend le premier fil dont TOUS les parents
 *    passent `canNavigate` (web-admin y branche `ROUTE_ACCESS`) : un
 *    enseignant arrivé par favori ne voit jamais un maillon qui le
 *    redirigerait vers l'accueil.
 * 4. LIBELLÉ DYNAMIQUE : tant que la page n'a rien poussé, le maillon courant
 *    affiche le `label` générique de la config (« Fiche élève », « Classe »…)
 *    — jamais vide, jamais un UUID, et c'est aussi ce qui reste affiché si le
 *    chargement échoue. Le libellé poussé est rattaché au `pathname` qui l'a
 *    produit : une page suivante ne peut pas hériter du nom de la précédente.
 * 5. SOUS-ÉTAT DE PAGE : `useBreadcrumb(label, { child })` ajoute un dernier
 *    maillon (ex. « Modifier l'emploi du temps ») et rend le maillon de la
 *    page cliquable vers son `pathname` SANS query string — la page doit donc
 *    porter ce sous-état dans l'URL (voir ClassTimetable.tsx, `?mode=edit`).
 * 6. NAVIGATION : chaque maillon est un `<Link>` React Router, jamais
 *    `history.back()` ; le dernier maillon n'est jamais un lien
 *    (`aria-current="page"`).
 *
 * HORS PÉRIMÈTRE (UX-85) : restaurer l'état de la page parente (filtre,
 * onglet, scroll) au clic sur un maillon — le lien mène à l'URL nue du parent.
 */

export interface BreadcrumbLink {
  label: string;
  to: string;
  /**
   * Paramètres de query string de la page COURANTE à reporter sur ce lien
   * (ex. `schoolYearId` du drill-down DSH-04) — pour un filtre qui fait partie
   * de l'identité de la page parente, pas pour restaurer un état d'interface.
   */
  keepSearchParams?: string[];
}

export interface BreadcrumbTrail {
  /** Maillons parents, du plus haut au plus proche — tous cliquables. */
  parents: BreadcrumbLink[];
  /**
   * Libellé de la page courante. Pour une route à segment dynamique, c'est
   * l'état transitoire affiché tant que la page n'a pas appelé
   * `useBreadcrumb` (et si son chargement échoue).
   */
  label: string;
  /** Mise en forme du libellé poussé par `useBreadcrumb` (ex. préfixe). */
  formatLabel?: (label: string) => string;
}

export interface BreadcrumbAlternative extends BreadcrumbTrail {
  /** Pattern de la page d'où l'on arrive, ex. `/teacher-schedule`. */
  from: string;
}

export interface BreadcrumbRoute extends BreadcrumbTrail {
  /** Pattern React Router, ex. `/students/:studentId`. */
  path: string;
  alternatives?: BreadcrumbAlternative[];
}

interface PageCrumb {
  pathname: string;
  label: string;
  child?: string;
}

interface BreadcrumbContextValue {
  routes: BreadcrumbRoute[];
  canNavigate?: (to: string) => boolean;
  origin: string | null;
  page: PageCrumb | null;
  setPage: (owner: object, page: PageCrumb | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

export interface BreadcrumbProviderProps {
  routes: BreadcrumbRoute[];
  /**
   * Un parent est-il atteignable par l'utilisateur courant ? Sert uniquement à
   * choisir entre les fils `alternatives` quand l'origine est inconnue.
   * Absent : tout est considéré atteignable.
   */
  canNavigate?: (to: string) => boolean;
  children: ReactNode;
}

interface CommittedLocation {
  pathname: string;
  origin: string | null;
}

export function BreadcrumbProvider({ routes, canNavigate, children }: BreadcrumbProviderProps) {
  const location = useLocation();
  const [pageState, setPageState] = useState<{ owner: object; page: PageCrumb } | null>(null);

  // Origine (dernier pathname DISTINCT) mémorisée par entrée d'historique.
  // Calculée pendant le rendu pour éviter un premier affichage sur le mauvais
  // fil ; idempotente (la même clé donne toujours le même résultat tant que
  // l'effet ci-dessous n'a pas validé la navigation).
  const originsRef = useRef(new Map<string, string | null>());
  const committedRef = useRef<CommittedLocation | null>(null);
  let origin = originsRef.current.get(location.key);
  if (origin === undefined) {
    const previous = committedRef.current;
    if (!previous) origin = null;
    else origin = previous.pathname === location.pathname ? previous.origin : previous.pathname;
    originsRef.current.set(location.key, origin);
  }

  useEffect(() => {
    committedRef.current = { pathname: location.pathname, origin: origin ?? null };
  }, [location.key, location.pathname, origin]);

  // Stable : `useBreadcrumb` l'a dans ses dépendances d'effet.
  const setPage = useCallback((owner: object, page: PageCrumb | null) => {
    setPageState((current) => {
      if (page) return { owner, page };
      // Ne retire que SON propre libellé : une page qui se démonte après que
      // la suivante a poussé le sien ne doit pas l'effacer.
      return current?.owner === owner ? null : current;
    });
  }, []);

  const value = useMemo<BreadcrumbContextValue>(
    () => ({ routes, canNavigate, origin: origin ?? null, page: pageState?.page ?? null, setPage }),
    [routes, canNavigate, origin, pageState, setPage],
  );

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

export interface UseBreadcrumbOptions {
  /**
   * Maillon supplémentaire APRÈS la page (sous-état porté par la query
   * string, ex. mode édition). Rend le maillon de la page cliquable vers son
   * `pathname` nu.
   */
  child?: string | null;
}

/**
 * Pousse le libellé RÉEL de la page courante (nom de l'élève, de la classe…)
 * à la place du libellé générique de la config. `label` vide/nul = rien de
 * poussé (donnée pas encore chargée) : le libellé de la config reste affiché.
 * Sans provider (test d'une page isolée), ne fait rien.
 */
export function useBreadcrumb(label: string | null | undefined, options: UseBreadcrumbOptions = {}) {
  const context = useContext(BreadcrumbContext);
  const setPage = context?.setPage;
  const { pathname } = useLocation();
  const [owner] = useState(() => ({}));
  const child = options.child ?? undefined;
  const trimmed = label?.trim() || null;

  useEffect(() => {
    if (!setPage) return;
    setPage(owner, trimmed ? { pathname, label: trimmed, child } : child ? { pathname, label: '', child } : null);
  }, [setPage, owner, pathname, trimmed, child]);

  useEffect(() => {
    if (!setPage) return;
    return () => setPage(owner, null);
  }, [setPage, owner]);
}

interface ResolvedCrumb {
  label: string;
  to?: string;
}

function selectTrail(route: BreadcrumbRoute, origin: string | null, canNavigate?: (to: string) => boolean) {
  const trails: BreadcrumbTrail[] = [route, ...(route.alternatives ?? [])];
  if (origin) {
    const fromOrigin = route.alternatives?.find((alt) => matchPath({ path: alt.from, end: true }, origin));
    if (fromOrigin) return fromOrigin;
    // Arrivé depuis un des parents du fil principal : c'est bien lui.
  }
  if (!canNavigate) return route;
  return trails.find((trail) => trail.parents.every((parent) => canNavigate(parent.to))) ?? route;
}

/** Calcule les maillons pour une `location` — exporté pour les tests. */
export function resolveBreadcrumbs(
  routes: BreadcrumbRoute[],
  { pathname, search }: { pathname: string; search: string },
  origin: string | null,
  page: PageCrumb | null,
  canNavigate?: (to: string) => boolean,
): ResolvedCrumb[] {
  const matches = matchRoutes(
    routes.map((route, index) => ({ path: route.path, id: String(index) })),
    pathname,
  );
  const match = matches?.[matches.length - 1];
  if (!match) return [];
  const route = routes[Number(match.route.id)];
  if (!route) return [];
  const trail = selectTrail(route, origin, canNavigate);

  const pageData = page && page.pathname === pathname ? page : null;
  const pushed = pageData?.label ? (trail.formatLabel ? trail.formatLabel(pageData.label) : pageData.label) : null;
  const currentParams = new URLSearchParams(search);
  const crumbs: ResolvedCrumb[] = trail.parents.map((parent) => {
    const kept = new URLSearchParams();
    for (const name of parent.keepSearchParams ?? []) {
      const value = currentParams.get(name);
      if (value !== null) kept.set(name, value);
    }
    const query = kept.toString();
    return { label: parent.label, to: query ? `${parent.to}?${query}` : parent.to };
  });
  const current = pushed ?? trail.label;
  if (pageData?.child) {
    crumbs.push({ label: current, to: pathname }, { label: pageData.child });
  } else {
    crumbs.push({ label: current });
  }
  return crumbs;
}

export interface BreadcrumbsProps {
  /** Libellé accessible du `<nav>` (web-parent passe sa traduction). */
  ariaLabel?: string;
}

export function Breadcrumbs({ ariaLabel = "Fil d'Ariane" }: BreadcrumbsProps) {
  const context = useContext(BreadcrumbContext);
  const location = useLocation();
  if (!context) return null;

  const crumbs = resolveBreadcrumbs(context.routes, location, context.origin, context.page, context.canNavigate);
  if (crumbs.length < 2) return null;

  return (
    // Même largeur que la carte qui suit : pleine largeur au-dessus d'une
    // `Card size="page"`, mais `max-w-md` au-dessus d'une `Card size="form"`
    // (centrée, 448px) — sinon le fil resterait collé à gauche, décroché de la
    // page qu'il décrit. Sans support de `:has()`, repli sur la pleine largeur.
    <nav
      aria-label={ariaLabel}
      className="ss-breadcrumbs mb-3 w-full [&:has(+.ss-card--form)]:max-w-md"
      data-testid="breadcrumbs"
    >
      <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm">
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            <li key={`${index}-${crumb.label}`} className="flex min-w-0 items-center gap-1.5">
              {crumb.to && !isLast ? (
                <Link
                  to={crumb.to}
                  className={
                    'ss-breadcrumb__link inline-flex max-w-[16rem] items-center rounded-sm text-text-tertiary ' +
                    'underline-offset-2 transition-colors hover:text-brand-700 hover:underline focus-visible:outline-none ' +
                    'focus-visible:ring-2 focus-visible:ring-brand-500'
                  }
                >
                  <span className="min-w-0 truncate">{crumb.label}</span>
                </Link>
              ) : (
                <span aria-current="page" className="max-w-[20rem] truncate font-medium text-text">
                  {crumb.label}
                </span>
              )}
              {isLast ? null : (
                <span aria-hidden="true" className="text-text-muted">
                  ›
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
